import React, { useState } from "react";
import logo from "../assets/pluginlive-logo.png";
import BrandShader from "./BrandShader";
import Icon from "./Icon";
import { TermsLink } from "./TermsDialog";
import googleLogo from "../assets/logos/google.png";
import tcsLogo from "../assets/logos/tcs.png";
import kpmgLogo from "../assets/logos/kpmg.png";
import jmLogo from "../assets/logos/jm-financial.png";
import dlfLogo from "../assets/logos/dlf.png";
import nseLogo from "../assets/logos/nse.png";
import lloydsLogo from "../assets/logos/lloyds.png";
import delhiveryLogo from "../assets/logos/delhivery.png";
import jbimsLogo from "../assets/logos/jbims.png";
import srmLogo from "../assets/logos/srm.png";
import graphicEraLogo from "../assets/logos/graphic-era.png";
import swadhaLogo from "../assets/logos/swadha.png";
import "../styles/ds-tokens.css";
import "../styles/auth.css";

/*
 * The design-system `login` screen shell (DS 11-tpo-journey § Auth / login
 * screen) shared by every auth screen: the light pane — logo, heading, the
 * screen's own form, footer links — on the left, and a dark brand pane of
 * customer testimonials and a logo wall on the right. One sign-in page serves
 * every product, so the testimonials mix recruiters and institutes.
 */

// ponytail: placeholder quotes, names and designations; the logos are real
// customers (from the website's logo walls). Replace each quote with a
// confirmed, attributable one before this leaves the design preview.
const TESTIMONIALS = [
  {
    logo: kpmgLogo,
    company: "KPMG",
    quote:
      "We ran a full analyst drive in a week, not a quarter. The shortlist arrived ranked and proctored, and every call was still ours.",
    name: "Meera Nair",
    role: "Head of Talent Acquisition",
  },
  {
    logo: tcsLogo,
    company: "TCS",
    quote:
      "One ranked list instead of five spreadsheets from five recruiters. Our hiring managers trust the shortlist.",
    name: "Rohan Deshpande",
    role: "Director of Recruiting",
  },
  {
    logo: jbimsLogo,
    company: "JBIMS",
    quote:
      "Every student sits a proctored assessment before a drive, so recruiters arrive already knowing who fits.",
    name: "Dr. Kavita Rao",
    role: "Training & Placement Officer",
  },
  {
    logo: swadhaLogo,
    company: "Swadha Foundation",
    quote:
      "Our learners now apply to roles that match their scores, and we can see every application through to the offer.",
    name: "Anjali Mehta",
    role: "Programme Director",
  },
];

const LOGOS = [
  { name: "Google", src: googleLogo },
  { name: "Tata Consultancy Services", src: tcsLogo },
  { name: "KPMG", src: kpmgLogo },
  { name: "JM Financial", src: jmLogo },
  { name: "NSE", src: nseLogo },
  { name: "Lloyds Technology Centre", src: lloydsLogo },
  { name: "Delhivery", src: delhiveryLogo },
  { name: "DLF", src: dlfLogo },
  { name: "JBIMS", src: jbimsLogo },
  { name: "SRM", src: srmLogo },
  { name: "Graphic Era University", src: graphicEraLogo },
  { name: "Swadha Foundation", src: swadhaLogo },
];

// Same figures as the marketing site's landing-page USPs (pluginlive-designs website).
const STATS = [
  { value: "150+", label: "companies" },
  { value: "7,500+", label: "institutes & NGOs" },
];

// Pyramid: 3 / 4 / 5 logos, widest row at the bottom.
const LOGO_ROWS = [LOGOS.slice(0, 3), LOGOS.slice(3, 7), LOGOS.slice(7)];

const SLIDE_MS = 5000;

/** One testimonial at a time, sliding left every SLIDE_MS; pauses on hover/focus. The active dot fills as its slide plays; dots pick a slide. */
function Testimonials() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  return (
    <div
      className="quote-carousel"
      data-paused={paused}
      style={{ "--quote-dwell": `${SLIDE_MS}ms` }}
      role="region"
      aria-roledescription="carousel"
      aria-label="Customer testimonials"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="quote-viewport">
        <ul
          className="quote-track"
          style={{ transform: `translateX(-${active * 100}%)` }}
        >
          {TESTIMONIALS.map((t, i) => (
            <li
              key={t.company}
              className="quote-card"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${TESTIMONIALS.length}`}
              aria-hidden={i !== active}
            >
              <img className="quote-logo" src={t.logo} alt={t.company} />
              <blockquote>&ldquo;{t.quote}&rdquo;</blockquote>
              <p className="quote-sign">
                <span className="quote-name">{t.name}</span>
                <span className="quote-role">
                  {t.role}, {t.company}
                </span>
              </p>
            </li>
          ))}
        </ul>
      </div>
      <div className="quote-dots">
        {TESTIMONIALS.map((t, i) => (
          <button
            key={t.company}
            type="button"
            className="quote-dot"
            aria-label={`Show testimonial ${i + 1}: ${t.company}`}
            aria-current={i === active ? "true" : undefined}
            onClick={() => setActive(i)}
            // The active dot's fill (CSS) times the slide: advance when it completes.
            // Reduced motion has no fill animation, so no auto-advance.
            onAnimationEnd={(e) => {
              if (e.animationName === "quote-fill") setActive((a) => (a + 1) % TESTIMONIALS.length);
            }}
          />
        ))}
      </div>
    </div>
  );
}

/** Feeds the pointer position to the card's spotlight as CSS vars — no React re-render per move. */
function trackPointer(e) {
  const card = e.currentTarget;
  const r = card.getBoundingClientRect();
  card.style.setProperty("--mx", `${e.clientX - r.left}px`);
  card.style.setProperty("--my", `${e.clientY - r.top}px`);
}

export default function AuthLayout({ title, subtitle, children, after }) {
  return (
    <main className="auth">
      <section className="login-pane">
        <div className="login-scroll">
          <div className="login-card">
            <a className="login-logo fade-up" href="https://www.pluginlive.com" aria-label="PluginLive home">
              <img src={logo} alt="PluginLive" />
            </a>
            <header className="login-head fade-up">
              <h1>{title}</h1>
              {subtitle && <p>{subtitle}</p>}
            </header>
            <div className="login-form fade-up">{children}</div>
            {after}
          </div>
        </div>
        <footer className="login-foot">
          <a
            href="https://www.pluginlive.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          <span className="sep" aria-hidden="true" />
          <TermsLink className="login-foot-link" />
        </footer>
      </section>

      <section className="brand-pane" aria-label="What our customers say">
        <div className="brand-card" onPointerMove={trackPointer}>
          <BrandShader />
          <div className="brand-spotlight" aria-hidden="true" />
          <div className="brand-body">
            <Testimonials />
          </div>
          <div className="brand-foot">
            <p className="brand-trust">
              <span>
                Trusted by{" "}
                {STATS.map((st, i) => (
                  <React.Fragment key={st.label}>
                    {i > 0 && (i === STATS.length - 1 ? " and " : ", ")}
                    <strong>{st.value}</strong> {st.label}
                  </React.Fragment>
                ))}
              </span>
            </p>
            <div className="logo-wall">
              {LOGO_ROWS.map((row) => (
                <ul key={row[0].name}>
                  {row.map((l) => (
                    <li key={l.name}>
                      <img src={l.src} alt={l.name} />
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

const MAIL_ICON = <Icon name="mail" />;
// Same glyph as the account-settings Password item (shell IconPassword).
const KEY_ICON = <Icon name="key" />;
const EYE = <Icon name="eye" />;
const EYE_OFF = <Icon name="eye-off" />;

/** A labelled field in the DS input shell, with its inline error. */
export function AuthField({ id, label, error, icon, children, action }) {
  return (
    <div className="auth-field">
      <label className="auth-field-label" htmlFor={id}>
        {label}
      </label>
      <div
        className="auth-input-shell"
        data-invalid={error ? "true" : undefined}
      >
        {icon && (
          <span className="icon" aria-hidden="true">
            {icon}
          </span>
        )}
        {children}
        {action}
      </div>
      {error && (
        <p className="auth-field-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function EmailField({
  id = "email",
  label = "Email",
  value,
  error,
  onChange,
  onKeyDown,
  placeholder = "you@company.com",
}) {
  return (
    <AuthField id={id} label={label} error={error} icon={MAIL_ICON}>
      <input
        id={id}
        name={id}
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder={placeholder}
        value={value}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </AuthField>
  );
}

export function PasswordField({
  id,
  label,
  value,
  error,
  onChange,
  onKeyDown,
  placeholder = "Enter your password",
  autoComplete = "current-password",
}) {
  const [shown, setShown] = useState(false);
  return (
    <AuthField
      id={id}
      label={label}
      error={error}
      icon={KEY_ICON}
      action={
        <button
          type="button"
          className="auth-input-action"
          aria-label={shown ? "Hide password" : "Show password"}
          aria-pressed={shown}
          onClick={() => setShown((s) => !s)}
        >
          {shown ? EYE_OFF : EYE}
        </button>
      }
    >
      <input
        id={id}
        name={id}
        type={shown ? "text" : "password"}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </AuthField>
  );
}
